import { useLocation } from "@tanstack/react-router";
import { SITE } from "@/lib/site";

/**
 * Navigation tiers:
 * - Tier A — `/lp/*`: strict funnel (stripped nav + minimal footer)
 * - Tier B — conversion pages: full nav, LP-style page components
 * - Tier C — info pages: full nav, standard layouts
 */

export type NavMode = "full" | "funnel" | "custom";
export type FooterMode = "full" | "funnel" | "minimal";

export type NavAnchor = { href: string; label: string };

export type LpPageConfig = {
  phone: { label: string; tel: string };
  navAnchors: NavAnchor[];
};

export const DEFAULT_LP_NAV_ANCHORS: NavAnchor[] = [
  { href: "#process", label: "Process" },
  { href: "#pricing", label: "Pricing" },
  { href: "#contact", label: "Contact" },
];

export const LP_PAGE_CONFIG: Record<string, LpPageConfig> = {
  "/lp/cavity-wall-insulation": {
    phone: { label: "01656 334698", tel: "tel:+441656334698" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
  "/lp/cavity-insulation-removal": {
    phone: { label: "01656 338507", tel: "tel:+441656338507" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
  "/solar-pv": {
    phone: { label: "01656 339844", tel: "tel:+441656339844" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
  "/boiler-upgrade-scheme": {
    phone: { label: "01656 339844", tel: "tel:+441656339844" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
  "/financing": {
    phone: { label: "01656 339844", tel: "tel:+441656339844" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
  "/design-solar": {
    phone: { label: "01656 339844", tel: "tel:+441656339844" },
    navAnchors: DEFAULT_LP_NAV_ANCHORS,
  },
};

/** Sticky header stack offset for on-page anchor scroll targets. */
export const FUNNEL_SCROLL_MT = "scroll-mt-28";

const NAV_MODE_BY_PREFIX: { prefix: string; mode: NavMode }[] = [
  { prefix: "/lp/", mode: "funnel" },
  { prefix: "/damp-survey", mode: "custom" },
];

export function getNavMode(pathname: string): NavMode {
  for (const { prefix, mode } of NAV_MODE_BY_PREFIX) {
    if (pathname.startsWith(prefix)) return mode;
  }
  return "full";
}

export function getFooterMode(pathname: string): FooterMode {
  if (pathname.startsWith("/lp/")) return "funnel";
  if (pathname === "/damp-survey") return "minimal";
  return "full";
}

export function isFunnelPath(pathname: string): boolean {
  return getNavMode(pathname) === "funnel";
}

export function getLpPageConfig(pathname: string): LpPageConfig | null {
  return LP_PAGE_CONFIG[pathname] ?? null;
}

export function getLpNavAnchors(pathname: string): NavAnchor[] {
  return getLpPageConfig(pathname)?.navAnchors ?? DEFAULT_LP_NAV_ANCHORS;
}

export type PagePhone = {
  label: string;
  tel: string;
  isLpOverride: boolean;
};

export function resolvePagePhone(pathname: string): PagePhone {
  const lp = getLpPageConfig(pathname);
  if (lp) {
    return { ...lp.phone, isLpOverride: true };
  }
  return {
    label: SITE.phoneSecondary,
    tel: SITE.phoneSecondaryTel,
    isLpOverride: false,
  };
}

/** Primary site phone for utility bar and non-LP contexts. */
export function resolveUtilityBarPhone(pathname: string): PagePhone {
  const lp = getLpPageConfig(pathname);
  if (lp) {
    return { ...lp.phone, isLpOverride: true };
  }
  return {
    label: SITE.phone,
    tel: SITE.phoneTel,
    isLpOverride: false,
  };
}

/** Solar / heat pump lead-magnet sticky bar: shown site-wide with default chrome. */
export function shouldHideLeadMagnetSticky(_pathname: string): boolean {
  return false;
}

/** Fixed mobile bottom CTA bar — hidden on cavity LP funnels only. */
export function shouldShowMobileBottomStickyCta(pathname: string): boolean {
  return (
    pathname !== "/lp/cavity-insulation-removal" &&
    pathname !== "/lp/cavity-wall-insulation"
  );
}

export function usePagePhone(): PagePhone {
  const { pathname } = useLocation();
  return resolvePagePhone(pathname);
}

export function useUtilityBarPhone(): PagePhone {
  const { pathname } = useLocation();
  return resolveUtilityBarPhone(pathname);
}

export function useNavMode(): NavMode {
  const { pathname } = useLocation();
  return getNavMode(pathname);
}

export function useLpNavAnchors(): NavAnchor[] {
  const { pathname } = useLocation();
  return getLpNavAnchors(pathname);
}

export function useHideLeadMagnetSticky(): boolean {
  const { pathname } = useLocation();
  return shouldHideLeadMagnetSticky(pathname);
}

export function useShowMobileBottomStickyCta(): boolean {
  const { pathname } = useLocation();
  return shouldShowMobileBottomStickyCta(pathname);
}
